export type Permission = "live-avatars.view" | "live-avatars.train";

export type ScenarioStatus = "draft" | "published";
export type PublishMode = "upsell" | "demo" | "full" | "hidden";
export type CtaType = "none" | "sales";
export type SessionMode = "full" | "lite";

/**
 * Runtime/backend provider that drives the avatar session. The scenario
 * content shape branches on this: `liveavatar` scenarios use the legacy
 * fields (`avatar_id`, `voice_id`, `context_block`, ...); `tavus`
 * scenarios use the `pal` block below and leave those fields empty.
 */
export type ProviderId = "liveavatar" | "tavus" | "did" | "kaltura";

/**
 * Tavus-specific content block. Field names follow the Tavus REST API
 * verbatim (`pal_id`, `face_id`, `voice_id`, `system_prompt`) so nothing
 * has to be translated at the HTTP boundary.
 */
export type TavusPalBlock = {
  /** Tavus pal (persona) id, synced by `saveScenario` on each save. */
  pal_id?: string;
  /** Default face for the pal — Tavus `default_face_id`. */
  face_id?: string;
  /**
   * Optional ElevenLabs voice override (BYO). When set, the edge function
   * injects the org's ElevenLabs API key into `layers.tts` at pal
   * create/update time. When unset, the face's replica default voice is used.
   *
   * Tavus' `tavus-auto` engine cannot pick catalog voices, so ElevenLabs is
   * the only supported override path for this app.
   */
  elevenlabs_voice_id?: string;
  /**
   * Composed prompt sent to Tavus as `system_prompt`. Built from the
   * form fields (persona / knowledge / instructions / source inputs) at
   * save time — same builder as the LiveAvatar flow.
   */
  system_prompt: string;
  /** Sent per-conversation as `custom_greeting`. */
  opening_text?: string;
  /**
   * Raw source inputs (uploads/urls/text) that were used to compose the
   * prompt. Kept so the editor can re-hydrate without going through
   * Tavus.
   */
  inputs?: ScenarioSourceItem[];
  /**
   * Editor-side mirrors of the LiveAvatar split fields. Tavus receives
   * only the composed `system_prompt`; we persist these three so the
   * form can round-trip without loss. Missing on scenarios saved before
   * this was introduced — fall back to `system_prompt`.
   */
  persona?: string;
  knowledge?: string;
  instructions?: string;
  /**
   * Interaction mode for this Tavus scenario. Stored on the pal (not on
   * a `context_block` — Tavus scenarios have none) so the editor and
   * runtime can branch on mode without introducing LA-only shapes.
   */
  interaction_mode?: InteractionMode;
  /**
   * Ordered list of `live_avatar_questionnaire` artifact IDs for this
   * Tavus scenario. Newest first — parity with the LA versioning
   * contract on `context_block.questionnaire_ids`.
   */
  questionnaire_ids?: string[];
  /** `live_avatar_roleplay` artifact versions, newest first. */
  roleplay_ids?: string[];
  /** Inner role-play scenario id this session plays and is scored against. */
  roleplay_scenario_id?: string;
  /**
   * When true, the learner-side session hides the camera-sharing control.
   * Tavus-only knob so trainers can match the HeyGen minimal control set.
   */
  disable_camera_sharing?: boolean;
  /**
   * When true, the learner-side session hides the screen-sharing control.
   * Tavus-only knob so trainers can match the HeyGen minimal control set.
   */
  disable_screen_sharing?: boolean;
  /**
   * Tavus knowledge-base documents provisioned for this scenario, paired to
   * the scenario source they came from. One entry per source we ever
   * registered, so a re-save reuses the existing `document_id` instead of
   * creating an orphan in Tavus.
   */
  knowledge_documents?: TavusKnowledgeDocument[];
  /**
   * `document_id` currently attached to the pal for retrieval (RAG). When
   * set, the knowledge text is not composed into `system_prompt` — Tavus
   * answers from the document instead.
   */
  knowledge_document_id?: string;
  /** Tavus `presentation` skill config, requires `knowledge_document_id`. */
  presentation?: TavusPresentationConfig;
  /**
   * Tavus `internet_search` skill. Pure on/off — no config and no document
   * dependency. The stored value is what Tavus confirmed at the last save.
   */
  internet_search?: TavusInternetSearchConfig;
};

/**
 * Pairing record between a scenario source and the Tavus document created
 * from it. `source_key` is derived from the source itself
 * (`upload:<object_id>` / `url:<url>`) so the link survives re-saves.
 */
export type TavusKnowledgeDocument = {
  source_key: string;
  document_id: string;
  document_name?: string;
  document_url?: string;
  /**
   * Last known Tavus processing status. `missing` is ours, not Tavus': it
   * marks an entry whose document no longer exists upstream (404) so the
   * editor can offer to register it again.
   */
  status?: "started" | "processing" | "ready" | "error" | "recrawling" | "missing";
  /** Tavus error code, only when `status === "error"`. */
  error_message?: string;
  synced_at?: string;
};

export type TavusPresentationConfig = {
  enabled?: boolean;
  slides_trigger?: "on_demand" | "walk_the_deck";
};

export type TavusInternetSearchConfig = {
  enabled?: boolean;
};

/**
 * D-ID-specific content block. Present only when `provider === "did"`.
 * Mirrors the shape of `TavusPalBlock` so both providers round-trip
 * through the shared adapter interface without special-casing.
 */
export type DidAgentBlock = {
  /** D-ID agent id, synced by `saveScenario` on each save (Pass 2). */
  agent_id?: string;
  /** D-ID presenter id (the face). Analogous to Tavus `face_id`. */
  presenter_id?: string;
  /** D-ID presenter transport kind. Realtime voice sessions require `expressive`. */
  presenter_type?: "expressive";
  /**
   * Voice provider as D-ID stored it, read back from the created agent.
   * Only `elevenlabs` is offered today; D-ID does not validate the
   * voice/provider pair on write, so this is never assumed locally.
   */
  voice_provider?: string;
  /** Voice id from `/tts/voices`, scoped by provider. */
  voice_id?: string;
  /**
   * Background applied to the presenter, by id from the local D-ID
   * background registry (`"none"` or a scene id). Resolved to an absolute
   * image URL by the `did` edge function at sync time.
   */
  background_id?: string;
  /**
   * Absolute https URL of the background image, when the trainer uploaded
   * one (`background_id: "custom"`). Built-in scenes resolve their URL from
   * the deployment origin instead.
   */
  background_url?: string;
  /** Composed prompt sent to D-ID as the agent's LLM system prompt. */
  system_prompt: string;
  /** Custom greeting used when starting a stream. */
  opening_text?: string;
  /** Raw source inputs used to compose the prompt. */
  inputs?: ScenarioSourceItem[];
  /** Editor-side mirrors of the split prompt fields for lossless round-trip. */
  persona?: string;
  knowledge?: string;
  instructions?: string;
  /**
   * Interaction mode. D-ID has no `context_block`, so — as with Tavus (`pal`)
   * and Kaltura — mode and questionnaire versions live on the provider block.
   */
  interaction_mode?: InteractionMode;
  /** Questionnaire versions, newest first. Refreshed by `saveScenario`. */
  questionnaire_ids?: string[];
  /** `live_avatar_roleplay` artifact versions, newest first. */
  roleplay_ids?: string[];
  /** Inner role-play scenario id this session plays and is scored against. */
  roleplay_scenario_id?: string;
  /**
   * D-ID knowledge base (`knw_...`) owned by this scenario. Created lazily on
   * the first document registration and never shared between scenarios.
   */
  knowledge_id?: string;
  /** Documents registered inside `knowledge_id`, paired to scenario sources. */
  knowledge_documents?: DidKnowledgeDocument[];
  /**
   * `true` → the agent answers only from the knowledge base (`rag-grounded`),
   * `false`/unset → documents blended with the model's general knowledge
   * (`rag-ungrounded`).
   */
  knowledge_grounded?: boolean;
  /**
   * `true` → the avatar can call the `present_document` client tool to show
   * the scenario's learner display source mid-session. Conversational mode
   * only, and only meaningful when `display.source` is set.
   */
  present_document?: boolean;
  /** D-ID tool id backing `present_document`, owned by this scenario. */
  presentation_tool_id?: string;
  /** D-ID tool id backing the `close_document` companion tool. */
  close_tool_id?: string;
  /**
   * All presentation client-tool ids owned by this scenario, in tool order:
   * `present_document`, `close_document`, and for PDF display sources
   * `show_page`, `read_current_page`. Supersedes the two fields above, which
   * are still written for backwards compatibility.
   */
  presentation_tool_ids?: string[];
};


/**
 * Pairing record between a scenario source and the document D-ID created from
 * it inside the scenario's knowledge base. Mirrors `TavusKnowledgeDocument`.
 */
export type DidKnowledgeDocument = {
  source_key: string;
  document_id: string;
  document_name?: string;
  document_url?: string;
  /** `missing` is ours: the document no longer exists upstream (404). */
  status?: "processing" | "ready" | "error" | "missing";
  error_message?: string;
  synced_at?: string;
};

export type KalturaKnowledgeSource = {
  source_key: string;
  kind: "upload" | "url";
  document_name?: string;
  /** Kaltura entry id for uploaded files. URL sources do not get an entry id. */
  entry_id?: string;
  /** Kaltura category created/used by `knowledge/upload` for uploaded files. */
  category_id?: string;
  /** Public URL registered as a web source, for URL sources only. */
  url?: string;
  status?: "ready" | "processing" | "error" | "missing";
  error_message?: string;
  synced_at?: string;
};

/**
 * Kaltura-specific content block. Kaltura models a scenario as two objects:
 * an `intellect` (the prompt payload) wrapped by an `agent` (intellect +
 * avatar ids). Both ids are provisioned by `saveScenario` and stored here.
 * Voice and visual are properties of the Kaltura avatar itself, so there is
 * no separate voice id to persist.
 */
export type KalturaAgentBlock = {
  /**
   * Legacy: the retired `sdk` | `agent` runtime switch. Sessions always run on
   * Kaltura's Agent product now; stored values are ignored.
   */
  runtime?: string;
  /** Kaltura agent id (uuid), synced by `saveScenario` on each save. */
  agent_id?: string;

  /** Numeric intellect id backing the agent's prompt content. */
  intellect_id?: number;
  /** Kaltura app guid returned with the agent — used by the runtime SDK. */
  app_guid?: string;
  /**
   * Kaltura avatar id — the scenario-owned visual+voice pair provisioned at
   * save time from `visual_id` / `voice_id`. Internal plumbing; the trainer
   * never picks it.
   */
  avatar_id?: string;
  /** Chosen face (Kaltura visual catalog item). */
  visual_id?: string;
  /** Chosen voice (Kaltura voice catalog item). */
  voice_id?: string;
  /** Composed prompt stored as the intellect's `base_directive`. */
  system_prompt: string;
  /** Opening phrase — Kaltura `opening_phrase`. */
  opening_text?: string;
  /** Forced response language — Kaltura `force_language`. */
  language?: string;
  /** Raw source inputs used to compose the prompt. */
  inputs?: ScenarioSourceItem[];
  /** Editor-side mirrors of the split prompt fields for lossless round-trip. */
  persona?: string;
  knowledge?: string;
  instructions?: string;
  /**
   * Interaction mode. Kaltura has no context block, so — like Tavus — the
   * mode and questionnaire versions live on the provider block itself.
   */
  interaction_mode?: InteractionMode;
  /** Newest first — `questionnaire_ids[0]` is the active version. */
  questionnaire_ids?: string[];
  /** `live_avatar_roleplay` artifact versions, newest first. */
  roleplay_ids?: string[];
  /** Inner role-play scenario id this session plays and is scored against. */
  roleplay_scenario_id?: string;
  /**
   * Numeric Kaltura knowledge record id, created lazily on the first knowledge
   * sync. Uploaded files are ingested into Kaltura; public URLs are registered
   * as web sources.
   */
  knowledge_id?: number;
  /** `source_key`s of the scenario sources synced into the record at last sync. */
  knowledge_source_keys?: string[];
  /** Kaltura-side metadata for each synced source. */
  knowledge_sources?: KalturaKnowledgeSource[];
  /** ISO timestamp of the last successful knowledge sync. */
  knowledge_synced_at?: string;
  /**
   * Live web search during the session (Kaltura `use_web_search`
   * capability). Conversational mode only.
   */
  web_search_enabled?: boolean;
  /**
   * Learner may share their screen with the avatar (Kaltura
   * `screen_share_analysis` capability). Kaltura takes no inbound video track:
   * the browser samples the shared surface and pushes stills for vision
   * analysis. Conversational mode only.
   */
  screen_share_enabled?: boolean;
  /**
   * Trainer opt-out: hide the learner's share-screen control. Screen sharing
   * is enabled by default for Kaltura, mirroring Tavus. Derived into
   * `screen_share_enabled` by the adapter.
   */
  disable_screen_sharing?: boolean;
  /**
   * Trainer lets the avatar show/hide (and, for a PDF, page through) the
   * scenario's display document via Kaltura client tools. Conversational only.
   * The document stays hidden until the avatar presents it.
   */
  present_document?: boolean;
  /**
   * Kaltura `/v1/tool/*` ids for the presentation tools, in creation order:
   * present_document, close_document, then show_page, read_current_page for a
   * PDF. Reused across saves so a re-save updates instead of piling up tools.
   */
  presentation_tool_ids?: string[];

};

export type ScenarioContent = {
  /**
   * Which provider runs this scenario at session time. Unset defaults to
   * `liveavatar` for backwards compatibility with pre-Tavus scenarios.
   */
  provider?: ProviderId;

  avatar_id?: string;
  voice_id?: string;
  /**
   * Session runtime mode. Read-only — provisioned externally in the artifact
   * JSON, no editor UI.
   * - "full" (default, also when unset): legacy `create_session_token` +
   *   `start_session` flow.
   * - "lite": BYO LiveKit flow (`start_byo_session`).
   */
  mode?: SessionMode;
  /** Trainer opt-in: always run this avatar on Powtoon BYO LiveKit, for every viewer incl. public links. */
  force_byo_livekit?: boolean;
  /**
   * Active LiveAvatar context id used by the player at runtime.
   * Intentionally mirrors `context_block.liveavatar_context_id`: that one is
   * what the trainer sync logic mutates, this one is what the session reads.
   */
  context_id?: string;
  language?: string;
  /**
   * Optional LiveAvatar LLM configuration id. Read-only in this app:
   * provisioned externally (directly in the artifact JSON) and forwarded
   * as-is to `create_session_token` when present. No editor UI.
   */
  llm_configuration_id?: string;
  context_block?: ScenarioContextBlock;
  status?: ScenarioStatus;
  publish_mode?: PublishMode;
  cta_type?: CtaType;
  cta_url?: string;
  redirect_url?: string;
  auto_start?: boolean;
  /** Optional document shown to the learner during the session. */
  display?: ScenarioDisplay;
  /** Trainer opt-in: show the AI compliance notice over the session. Missing = off. */
  show_ai_notice?: boolean;
  /** Tavus-specific block — present only when `provider === "tavus"`. */
  pal?: TavusPalBlock;
  /** D-ID-specific block — present only when `provider === "did"`. */
  did?: DidAgentBlock;
  kaltura?: KalturaAgentBlock;
  [key: string]: unknown;
};

export type ScenarioDisplayLayout = "side-by-side" | "bubble";
export type ScenarioDisplaySource =
  | { kind: "upload"; object_id: string; file_name: string; mime?: string }
  | { kind: "url"; url: string };
export type ScenarioDisplay = {
  layout: ScenarioDisplayLayout;
  /** Side the avatar visually occupies. For side-by-side this is the avatar column;
   *  for bubble this is which top corner the bubble sits in. Defaults to "right". */
  avatar_position?: "left" | "right";
  source?: ScenarioDisplaySource;
};

export type InteractionMode = "multiple_choice" | "role_play" | "conversational";

export type ScenarioSourceItem =
  | {
      kind: "upload";
      object_id: string;
      file_name: string;
      mime?: string;
      size?: number;
      /** Cached Gemini Files API URI for this attachment. Disposable — re-created from storage when expired. */
      gemini_file_uri?: string;
      /** ISO timestamp when the cached Gemini file URI expires. */
      gemini_expires_at?: string;
    }
  | { kind: "url"; url: string }
  | { kind: "text"; raw_text: string };

/**
 * A free-form question. Every entry in `answers` is a valid answer item.
 * `amount_of_correct_answers_to_pass` is the minimum number the learner
 * must produce to be marked correct for this question.
 */
export type MCQAnswer = { text: string };
export type MCQQuestion = {
  id: string;
  question: string;
  amount_of_correct_answers_to_pass: number;
  answers: MCQAnswer[];
};
/**
 * A questionnaire is an ordered list of questions plus the overall pass
 * threshold (`pass_rate`, percentage 1..100 of questions that must be
 * passed).
 */
export type MCQConfig = {
  id: string;
  pass_rate: number;
  questions: MCQQuestion[];
};

export type ScenarioContextBlock = {
  /**
   * LiveAvatar context id owned by the trainer sync flow in
   * `saveScenario.ts`. Mirrored to `ScenarioContent.context_id` on save.
   */
  liveavatar_context_id?: string;
  interaction_mode?: InteractionMode;
  inputs?: ScenarioSourceItem[];
  /**
   * Ordered list of `live_avatar_questionnaire` artifact IDs for this scenario.
   * Newest first — `questionnaire_ids[0]` is the current/active version.
   * Older versions are retained as history and never deleted.
   */
  questionnaire_ids?: string[];
  /**
   * Ordered list of `live_avatar_roleplay` artifact IDs for this scenario,
   * newest first. The artifact is the only copy of the role-play content —
   * the editor loads it and the Powtoon BE scoring job grades against it.
   */
  roleplay_ids?: string[];
  /** Inner role-play scenario id this session plays and is scored against. */
  roleplay_scenario_id?: string;
  persona: string;
  knowledge: string;
  instructions: string;
  opening_text: string;
  generated_prompt: string;
};

export type RolePlayCompetencyMetric = {
  id: string;
  name: string;
  description?: string;
};

export type RolePlayAssessment = {
  competency_metrics: RolePlayCompetencyMetric[];
  success_triggers: string[];
  red_flags: string[];
  termination_conditions: string[];
  /** Optional learner-turn limit (3–15). Missing on legacy records → runtime default 8. */
  max_turns?: number;
};

export type RolePlayScenario = {
  id: string;
  avatar_role: string;
  avatar_personality: string;
  emotional_state: string;
  hidden_information: string;
  learner_role: string;
  emotional_progression: string;
  framing_script?: string;
  assessment: RolePlayAssessment;
};

export type RolePlayConfig = {
  version: 1;
  goal: string;
  scenarios: RolePlayScenario[];
};

export type Artifact<TContent> = {
  id: string;
  owner: number;
  application: "live-avatars" | string;
  object_type?: string;
  name: string;
  description?: string;
  thumb_url?: string;
  created: string;
  modified: string;
  project_id: string | null;
  is_deleted?: boolean;
  content: TContent;
};

export type Scenario = Artifact<ScenarioContent>;

export type QuestionnaireContent = MCQConfig;
export type Questionnaire = Artifact<QuestionnaireContent>;

/**
 * Versioned role-play artifact (`object_type: "live_avatar_roleplay"`). The
 * Powtoon BE scoring job resolves it from the project behind the transcript,
 * so the client only has to keep it up to date and remember its id.
 */
export type RolePlayArtifact = Artifact<RolePlayConfig | { role_play: RolePlayConfig }>;

/** Back-compat reader: accepts `{ role_play }` or a flat `RolePlayConfig`. */
export function readRolePlayConfig(a: RolePlayArtifact): RolePlayConfig | null {
  const c = a.content as unknown as { role_play?: unknown } & Record<string, unknown>;
  const raw = (c?.role_play ?? c) as Partial<RolePlayConfig> | undefined;
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.scenarios)) return null;
  return {
    version: 1,
    goal: typeof raw.goal === "string" ? raw.goal : "",
    scenarios: raw.scenarios as RolePlayConfig["scenarios"],
  };
}

/**
 * Back-compat reader. Accepts the new `{ questionnaire }` content, the
 * intermediate `{ mcq }` shape, or a flat `MCQConfig` at root. Returns
 * `null` for legacy MCQ payloads that don't match the new shape so the
 * editor starts blank.
 */
export function readQuestionnaireMCQ(q: Questionnaire): MCQConfig | null {
  const c = q.content as unknown as {
    questionnaire?: unknown;
    mcq?: unknown;
  } & Record<string, unknown>;
  const raw = c?.questionnaire ?? c?.mcq ?? c;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<MCQConfig> & Record<string, unknown>;
  if (!Array.isArray(r.questions)) return null;
  // Legacy shape: questions have `type`/`correct` flags but no
  // `amount_of_correct_answers_to_pass`. Treat as empty so the trainer
  // regenerates.
  const looksLegacy =
    r.questions.some(
      (qq) =>
        qq &&
        typeof qq === "object" &&
        ("type" in (qq as object) ||
          "amount_of_correct_answers_to_pass" in (qq as object) === false),
    ) &&
    r.questions.every(
      (qq) =>
        qq && typeof qq === "object" && !("amount_of_correct_answers_to_pass" in (qq as object)),
    );
  if (looksLegacy) return null;
  return {
    id: typeof r.id === "string" ? r.id : "",
    pass_rate: typeof r.pass_rate === "number" ? r.pass_rate : 80,
    questions: r.questions as MCQQuestion[],
  };
}

export type ScoreBreakdownEntry = {
  question_id: string;
  score: string;
  comment: string;
};

export type ScoreBreakdownItem = { score: string; comment: string };

type ScoringJobStatus = "created" | "inProgress" | "ready" | "error";

export type ScoringJobResponse =
  | { status: "created" | "inProgress"; id?: string }
  | { status: "error"; error_message: string; id?: string }
  | {
      status: "ready";
      id?: string;
      result: {
        score: "pass" | "fail";
        grade?: number;
        score_breakdown: Record<string, ScoreBreakdownItem>;
      };
    };

export type ScoreResult = {
  pass: boolean;
  grade?: number;
  breakdown: ScoreBreakdownEntry[];
  duration_seconds?: number;
};

export type CreateScenarioInput = {
  name: string;
  description?: string;
  project_id?: string;
  thumb_url?: string;
  content: ScenarioContent;
};
export type UpdateScenarioInput = Partial<CreateScenarioInput>;

export type UpdateScenarioMetaInput = {
  name?: string;
  description?: string;
  project_id?: string;
  thumb_url?: string;
};

export type Attachment = {
  id: string;
  owner: number;
  object_type: string;
  name: string;
  /** Canonical download URL returned by Powtoon when `include_content=true`. */
  file?: string | null;
  /** Legacy alias — some responses use this name. */
  file_url?: string | null;
  ai_project?: string;
  created?: string;
  modified?: string;
};

export type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type UserGroup = {
  id: string;
  title: string;
  parent_group_id?: string;
  creation_date?: string;
  created_by?: number;
  user_count?: number | null;
  thumb_url?: string | null;
};

export type ShareGranteeType = "user" | "group" | "organization" | "lead";

export type ShareUserExtra = {
  email?: string;
  first_name?: string;
  last_name?: string;
  thumb_url?: string;
};

export type Share = {
  id: string;
  grantee_id: string;
  grantee_type: ShareGranteeType;
  permissions: string[];
  extra_data: ShareUserExtra | Record<string, unknown> | null;
  created_date: string;
  last_modified_date: string;
};
